import React, { useEffect, useState, useMemo } from 'react';
import { inject, observer } from 'mobx-react';
import { Form, Formik, FormikHelpers } from 'formik';
import merge from 'lodash.merge';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';

import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Collapse from '@material-ui/core/Collapse';
import Link from '@material-ui/core/Link';
import DropDownIcon from '@material-ui/icons/ArrowDropDownCircle';
import TextField from '@material-ui/core/TextField';

import compose from 'helpers/compose';
import { scrollToTop, getYearsAndMonthsFromLuxon } from 'helpers/helpers';
import Background from 'components/Layout/Background';
import { CalculationMode } from 'common/interfaces';
import Graph from 'components/Graph/ColumnGraph';
import withoutEnterSubmit from 'components/withoutEnterSubmit';
import FormSelectField from 'components/FormFields/FormSelectField';
import FormNumberField from 'components/FormFields/FormNumberField';
import FormDateSelects from 'components/FormFields/FormDateSelects';
import Switch from 'components/FormFields/Switch';
import Info from 'components/Tooltip/Info';
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';
import InflationRateField from 'components/FormFields/InflationRateField';

import { RentStore } from 'stores/workflowStores/finances/rent/rentStore';
import { RootStore } from 'stores/rootStore';
import { getInitialValues, RentData } from 'stores/workflowStores/finances/rent/model';
import { TargetOptions } from 'stores/workflowStores/finances/targets/model';

import { useStyles } from 'pages/Accounts/Edit/components/Rent/styles/rentEditStyles';
import { RentModalsStore } from 'stores/workflowStores/finances/rentModalsStore';
import { PersonType, AccountMode, Currency } from 'enums';

const FormWithoutEnterSubmit = withoutEnterSubmit(Form);

interface Props {
  flow: CalculationMode;
  resetFlow: (flow: CalculationMode) => void;
  mode: AccountMode;
  onToNextTab: () => void;
  onToBackTab: () => void;
  classes: Record<string, string>;
  rootStore: RootStore;
  accountId: string;
  currency: Currency;
  rentModalsStore: RentModalsStore;
  defaultInflation: number;
  defaultInflationType: string;
  personType: PersonType;
  options: TargetOptions;
  detailMode?: boolean;
  plan?: boolean;
  reset: boolean;
  refresh: () => void;
}

const Rent: React.FC<Props> = ({
  flow,
  resetFlow,
  mode,
  rootStore,
  accountId,
  currency,
  rentModalsStore,
  defaultInflation,
  defaultInflationType,
  options,
  plan,
  detailMode,
  personType,
  reset,
  refresh,
}) => {
  const [store] = useState(new RentStore(rootStore, accountId, rentModalsStore, detailMode));
  const { data, handleSubmit } = store; //chart,
  const { rentFormKey, setRentSubmitting } = rentModalsStore;
  const { t } = useTranslation();
  const translatedCurrency = t(`currency_${currency}`);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showDetail, setShowDetail] = useState(!plan && flow === CalculationMode.REQUIRED);
  const [showZeroRequiredRent, setShowZeroRequiredRent] = useState(false);
  const classes = useStyles();

  const minDate = useMemo(
    () =>
      mode === AccountMode.LTIP && store.data?.minStart
        ? getYearsAndMonthsFromLuxon(DateTime.fromSQL(store.data.minStart))
        : getYearsAndMonthsFromLuxon(DateTime.local()),
    [store.data?.minStart, mode],
  );
  const maxDate = useMemo(
    () => (store.data?.maxStart ? getYearsAndMonthsFromLuxon(DateTime.fromSQL(store.data.maxStart)) : undefined),
    [store.data?.maxStart],
  );

  useEffect(() => {
    scrollToTop('.MuiDialogContent-root');
    store.fetchRent(reset ? flow : undefined);
  }, [store, flow, reset]);

  useEffect(() => {
    if (flow === CalculationMode.REQUIRED && data?.requiredPortfolioRent.value === 0) {
      setShowZeroRequiredRent(true);
    }
  }, [flow, data?.requiredPortfolioRent.value]);

  const submit = async (values: RentData, formikHelpers: FormikHelpers<RentData>) => {
    setRentSubmitting(true);
    const ok = await handleSubmit(values, formikHelpers);
    setRentSubmitting(false);
    if (ok) {
      refresh();
      rentModalsStore.handleCloseRent();
    }
  };

  const initialValues = getInitialValues(currency);
  initialValues.inflation = {
    enabled: true,
    rate: defaultInflation,
    valuation: flow === CalculationMode.MANUAL || flow === CalculationMode.REQUIRED,
    type: defaultInflationType,
  };
  initialValues.portfolioRentCalculationMode = flow;

  return (
    data && (
      <Background inside>
        {store.calculating ? (
          ''
        ) : (
          <Formik initialValues={merge({}, initialValues, data)} onSubmit={submit} enableReinitialize>
            {(formikProps) => (
              <FormWithoutEnterSubmit id={rentFormKey}>
                <Grid container alignItems="center" alignContent="center" spacing={0}>
                  <Grid container item spacing={0}>
                    <Grid container item xs={7}>
                      <Grid item xs={12}>
                        {/*flow === CalculationMode.REQUIRED && (

                      )*/}
                        <Typography>&nbsp;</Typography>
                      </Grid>
                      {data.netIncome?.value > 0 ? (
                        <Grid item xs={5}>
                          <FormSelectField
                            name="portfolioRentCalculationMode"
                            label={t('finQuestionnary_rent_calculationMode')}
                            options={data.options?.portfolioRentCalculationMode.map((mode) => ({
                              value: mode,
                              label: t([`calculationMode_rent_allowance_${mode}`, `calculationMode_rent_${mode}`]),
                            }))}
                            afterChange={() => {
                              resetFlow(formikProps.values.portfolioRentCalculationMode);
                              store.fetchRent(formikProps.values.portfolioRentCalculationMode);
                            }}
                          />
                        </Grid>
                      ) : flow === CalculationMode.REQUIRED || flow === CalculationMode.MANUAL ? (
                        <Grid item xs={5}>
                          <Typography variant="h3">{t('finQuestionnary_howMuchMoneyFromRent')}</Typography>
                        </Grid>
                      ) : (
                        ''
                      )}

                      <Grid item xs={5}>
                        {flow === CalculationMode.REQUIRED && (
                          <>
                            <FormNumberField
                              name="requiredPortfolioRent.value"
                              endAdornment={translatedCurrency}
                              label={t(`finQuestionnary_rent_monthly_${flow}`)}
                              readOnly
                            />
                            <TextField
                              name={`requiredPortfolioRent.currency`}
                              type="hidden"
                              style={{ visibility: 'hidden' }}
                              value={currency}
                            />
                          </>
                        )}
                        {flow === CalculationMode.MANUAL && (
                          <>
                            <FormNumberField
                              name="plannedPortfolioRent.value"
                              endAdornment={translatedCurrency}
                              label={t(`finQuestionnary_rent_monthly_${flow}`)}
                              afterBlur={
                                formikProps.values.plannedRequiredRent.value > 0
                                  ? () => store.recalculate(formikProps.values, formikProps)
                                  : undefined
                              }
                            />
                            <TextField
                              name={`plannedPortfolioRent.currency`}
                              type="hidden"
                              style={{ visibility: 'hidden' }}
                              value={currency}
                            />
                          </>
                        )}
                        {flow === CalculationMode.CALCULATED && (
                          <Typography className={classes.note}>
                            {t(
                              plan
                                ? 'finQuestionnary_rent_calculatedAllowanceInfo'
                                : 'finQuestionnary_rent_calculatedInfo',
                            )}
                          </Typography>
                        )}
                      </Grid>
                    </Grid>
                    <Grid container item xs={5} style={{ marginLeft: '-108px' }}>
                      <Grid item xs={12}>
                        <Typography>
                          {t('finQuestionnary_rent_from')}
                          {mode === AccountMode.LTIP && <Info content={t('finQuestionnary_rent_from_LTIP')} inline />}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <FormDateSelects name="start" minDate={minDate} maxDate={maxDate} fast={false} />
                      </Grid>{' '}
                    </Grid>
                  </Grid>

                  <Link
                    className={classes.showLink}
                    id="toggle-advanced"
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault();
                      setShowAdvanced(!showAdvanced);
                    }}
                  >
                    <span className={classes.show}>
                      {t('general_advanced')}
                      <DropDownIcon className={`${showAdvanced ? classes.shownIcon : ''}`} />
                    </span>
                  </Link>

                  <Collapse in={showAdvanced} style={{ width: '100%' }}>
                    <Grid container item spacing={0}>
                      <Grid container item xs={7}>
                        <Grid item xs={7}>
                          <Switch
                            name={`inflation.enabled`}
                            label={t('general_useInflation')}
                            tooltipContent={t('additionalTargets_inflation_tooltip')}
                            disabled={flow === CalculationMode.CALCULATED}
                            afterChange={() => {
                              if (!formikProps.values.inflation.enabled) {
                                formikProps.setFieldValue('inflation.valuation', false);
                                formikProps.setFieldValue('inflation.type', '');
                              }
                            }}
                          />
                        </Grid>
                        <Grid item xs={3}>
                          <InflationRateField
                            name="inflation.rate"
                            label={t('general_annualInflation')}
                            {...(!formikProps.values.inflation.enabled || flow === CalculationMode.CALCULATED
                              ? { disabled: true }
                              : {})}
                          />
                        </Grid>
                        <Grid item xs={2} />
                        <Grid item xs={7}>
                          <Switch
                            name={`inflation.valuation`}
                            label={t('additionalTargets_useInflation')}
                            tooltipContent={t('additionalTargets_useInflation_tooltip')}
                            disabled={!formikProps.values.inflation.enabled || flow === CalculationMode.CALCULATED}
                          />
                        </Grid>
                        <Grid item xs={3}>
                          <FormSelectField
                            name="inflation.type"
                            options={options.inflationType.map((type) => ({
                              value: type,
                              label: t(`general_inflationType_${type}`),
                            }))}
                            fast={false}
                            disabled={
                              !formikProps.values.inflation?.enabled || !formikProps.values.inflation?.valuation
                            }
                            tooltipContent={t('additionalTargets_useInflationType_tooltip')}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Collapse>

                  <Link
                    className={classes.showLink}
                    id="toggle-estimate-link"
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault();
                      setShowDetail(!showDetail);
                    }}
                  >
                    <span className={classes.show}>
                      {t('general_detail')}
                      <DropDownIcon className={`${showDetail ? classes.shownIcon : ''}`} />
                    </span>
                  </Link>

                  <Collapse in={showDetail}>
                    <Grid container>
                      {personType !== PersonType.LEGAL && (
                        <>
                          <Grid item xs={12} />

                          <Grid container item spacing={0}>
                            <Grid container item xs={7}>
                              <Grid item xs={5}>
                                <Typography variant="h3">{t('finQuestionnary_aboutFamily_tabName')}:</Typography>
                              </Grid>
                              <Grid item xs={5}>
                                <FormNumberField
                                  name="netIncome.value"
                                  endAdornment={translatedCurrency}
                                  label={t('finQuestionnary_aboutFamily_totalIncome')}
                                  readOnly
                                />
                              </Grid>

                              <Grid item xs={12}>
                                &nbsp;
                              </Grid>
                            </Grid>
                          </Grid>

                          <Grid item xs={12}>
                            <Typography variant="h3">{t('finQuestionnary_rent_ourGuessFrom')}:</Typography>
                          </Grid>

                          <Grid container item spacing={0}>
                            <Grid container item xs={7}>
                              <Grid item xs={5}>
                                <FormSelectField
                                  name="requiredRentCalculationMode"
                                  label={t('finQuestionnary_pension_guess')}
                                  afterChange={
                                    formikProps.values.requiredRentCalculationMode === CalculationMode.CALCULATED ||
                                    (formikProps.values.requiredRentCalculationMode === CalculationMode.MANUAL &&
                                      formikProps.values.plannedRequiredRent?.value > 0)
                                      ? () => store.recalculate(formikProps.values, formikProps)
                                      : () => {}
                                  }
                                  options={data.options?.requiredRentCalculationMode.map((mode) => ({
                                    value: mode,
                                    label: t(`calculationMode_${mode}`),
                                  }))}
                                />
                              </Grid>

                              <Grid item xs={5}>
                                {formikProps.values.requiredRentCalculationMode === CalculationMode.CALCULATED && (
                                  <>
                                    <FormNumberField
                                      name="calculatedRequiredRent.value"
                                      endAdornment={translatedCurrency}
                                      label={t('finQuestionnary_pension_rentIncome')}
                                      readOnly
                                      tooltipContent={t('tooltip_financeRent_neededPension')}
                                    />
                                    <TextField
                                      name={`calculatedRequiredRent.currency`}
                                      type="hidden"
                                      style={{ visibility: 'hidden' }}
                                      value={currency}
                                    />
                                  </>
                                )}
                                {formikProps.values.requiredRentCalculationMode === CalculationMode.MANUAL && (
                                  <>
                                    <FormNumberField
                                      name="plannedRequiredRent.value"
                                      endAdornment={translatedCurrency}
                                      label={t('finQuestionnary_pension_rentIncome')}
                                      afterBlur={
                                        formikProps.values.plannedRequiredRent.value > 0 &&
                                        ((flow === CalculationMode.MANUAL &&
                                          formikProps.values.plannedPortfolioRent.value > 0) ||
                                          flow !== CalculationMode.MANUAL)
                                          ? () => store.recalculate(formikProps.values, formikProps)
                                          : undefined
                                      }
                                    />
                                    <TextField
                                      name={`plannedRequiredRent.currency`}
                                      type="hidden"
                                      style={{ visibility: 'hidden' }}
                                      value={currency}
                                    />
                                  </>
                                )}
                              </Grid>

                              <Grid item xs={12}>
                                &nbsp;
                              </Grid>

                              <Grid item xs={5} />
                              <Grid item xs={5}>
                                <>
                                  <FormNumberField
                                    name="retirementIncome.value"
                                    endAdornment={translatedCurrency}
                                    label={t('finQuestionnary_rent_actualRent')}
                                    tooltipContent={t('tooltip_financeRent_withoutPortfolio')}
                                    readOnly
                                  />
                                  <TextField
                                    name={`retirementIncome.currency`}
                                    type="hidden"
                                    style={{ visibility: 'hidden' }}
                                    value={currency}
                                  />
                                </>
                              </Grid>

                              <Grid item xs={12}>
                                &nbsp;
                              </Grid>

                              <Grid item xs={5} />
                              <Grid item xs={5}>
                                <FormNumberField
                                  name="requiredPortfolioRent.value"
                                  endAdornment={translatedCurrency}
                                  label={t('finQuestionnary_portfolioRent_monthly')}
                                  readOnly
                                />
                                <TextField
                                  name={`requiredPortfolioRent.currency`}
                                  type="hidden"
                                  style={{ visibility: 'hidden' }}
                                  value={currency}
                                />
                              </Grid>
                            </Grid>

                            {data && (
                              <Grid item xs={5} style={{ paddingLeft: '16px' }}>
                                <Graph
                                  loading={formikProps.isSubmitting}
                                  currency={currency}
                                  xCategories={undefined}
                                  data={data}
                                />
                              </Grid>
                            )}
                          </Grid>
                        </>
                      )}
                    </Grid>
                  </Collapse>
                </Grid>
              </FormWithoutEnterSubmit>
            )}
          </Formik>
        )}
        <ConfirmDialog noHeader isOpen={showZeroRequiredRent} confirm={() => setShowZeroRequiredRent(false)}>
          {t('finQuestionnary_portfolioRent_monthly_zero')}
        </ConfirmDialog>
      </Background>
    )
  );
};

export default compose(inject('rootStore'), observer)(Rent);
